import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { useSession } from "../../session/SessionContext";
import styles from "./AdminNav.module.css";

const RolesAdministerPermission = "roles.administer";
const EntitlementAdministerPermission = "entitlement.administer";
const AccountsAdministerPermission = "accounts.administer";
// Only the permission to see every request opens the Requests screen; an approver without it works from My Requests.
const RequestApprovePermission = "request.approve.any";
const AuditReadPermission = "audit.read";
const SettingsWritePermission = "settings.write";
const GroupsAdministerPermission = "groups.administer";
const BedrockModelsManagePermission = "bedrock.models.manage";
const ProvidersAdministerPermission = "providers.administer";
const ConfigurationPermission = "configuration.import-export";

const linkClass = ({ isActive }: { isActive: boolean }): string => (isActive ? styles.linkActive : styles.link);

/**
 * The administration sub-navigation shown beneath the header when the user is inside the admin
 * section. It exposes the remaining management views while keeping the top-level tabs focused on the
 * three major app sections.
 */
export function AdminNav(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const location = useLocation();

  if (snapshot.status !== "signed-in") {
    return null;
  }

  if (!location.pathname.startsWith("/admin")) {
    return null;
  }

  const permissions = snapshot.permissions;
  // The System link leads only to the System Settings tabs, so it needs the permission behind at least one of them.
  const hasAnySystemTab = [
    AuditReadPermission,
    SettingsWritePermission,
    ConfigurationPermission,
    AccountsAdministerPermission,
  ].some((permission) => permissions.includes(permission));
  // The Providers link leads to the Providers tabs, which need provider administration, the Bedrock models permission, or settings.write (Write Safety).
  const hasAnyProviderTab = [ProvidersAdministerPermission, BedrockModelsManagePermission, SettingsWritePermission].some((permission) => permissions.includes(permission));
  const hasAnyAdmin = [
    EntitlementAdministerPermission,
    ProvidersAdministerPermission,
    RolesAdministerPermission,
    AccountsAdministerPermission,
    AuditReadPermission,
    RequestApprovePermission,
    SettingsWritePermission,
    GroupsAdministerPermission,
    BedrockModelsManagePermission,
    ConfigurationPermission,
  ].some((permission) => permissions.includes(permission));

  if (!hasAnyAdmin) {
    return null;
  }

  return (
    <nav className={styles.nav} aria-label={t("nav.adminSectionLabel")} data-toast-anchor>
      {permissions.includes(RequestApprovePermission) && (
        <NavLink to="/admin/requests" className={linkClass}>
          {t("nav.adminRequests")}
        </NavLink>
      )}
      {permissions.includes(EntitlementAdministerPermission) && (
        <NavLink to="/admin/users" className={linkClass}>
          {t("nav.adminEntitlement")}
        </NavLink>
      )}
      {permissions.includes(GroupsAdministerPermission) && (
        <NavLink to="/admin/groups" className={linkClass}>
          {t("groupAdmin.nav")}
        </NavLink>
      )}
      {permissions.includes(RolesAdministerPermission) && (
        <NavLink to="/admin/roles" className={linkClass}>
          {t("nav.adminRoles")}
        </NavLink>
      )}
      {hasAnyProviderTab && (
        <NavLink to="/admin/providers" className={linkClass}>
          {t("nav.adminProviders")}
        </NavLink>
      )}
      {hasAnySystemTab && (
        <NavLink to="/admin/system" className={linkClass}>
          {t("nav.adminSystem")}
        </NavLink>
      )}
    </nav>
  );
}
